import { useEffect, useState } from "react";
import { Quote, Star, User } from "lucide-react";
import { Reveal } from "./Reveal";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { api, STATIC_TESTIMONIALS } from "@/lib/api";

const formatTestimonials = (list) =>
  (list || []).map((t) => ({
    ...t,
    quote: t.quote || t.content || "",
    img: t.image || t.img || "",
  }));

const DUMMY_NAMES = new Set(["Ananya Sharma", "Rahul Menon", "Priya Nair"]);

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState(() => formatTestimonials(STATIC_TESTIMONIALS));

  useEffect(() => {
    api
      .listTestimonials()
      .then(({ testimonials: incoming }) => {
        const valid = (incoming || []).filter((t) => !DUMMY_NAMES.has(t.name));
        if (valid.length > 0) {
          setTestimonials(formatTestimonials(valid));
        }
      })
      .catch(() => {});
  }, []);

  if (testimonials.length === 0) return null;

  return (
  <section id="testimonials" className="relative bg-white py-16 md:py-20" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div className="max-w-2xl">
          <Reveal>
            <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
              Client Voices
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-navy-900 md:text-4xl lg:text-5xl">
              What Our Clients Say
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-3 text-base text-slate-600 md:text-lg">
              Real experiences from businesses that trust Biz360 Compliance.
            </p>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1}>
        <Carousel opts={{ align: "start", loop: true }} className="mt-14" data-testid="testimonials-carousel">
          <CarouselContent>
            {testimonials.map((t, i) => (
              <CarouselItem key={t.name} className={testimonials.length <= 2 ? "md:basis-1/2" : "md:basis-1/2 lg:basis-1/3"} data-testid={`testimonial-card-${i}`}>
                <figure className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_20px_44px_rgba(15,30,60,0.12)]">
                  <Quote className="h-8 w-8 text-gold-400" />
                  <div className="mt-4 flex gap-0.5" aria-label={`${t.rating || 5} out of 5`}>
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star
                        key={s}
                        className={`h-4 w-4 ${s < (t.rating || 5) ? "fill-gold-500 text-gold-500" : "text-slate-300"}`}
                      />
                    ))}
                  </div>
                  <blockquote className="mt-4 flex-1 text-base leading-relaxed text-navy-800">
                    "{t.quote || t.content}"
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
                    {t.img ? (
                      <img src={t.img} alt={t.name} className="h-11 w-11 rounded-full object-cover ring-2 ring-emerald2-500/30" loading="lazy" />
                    ) : (
                      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500 ring-2 ring-slate-200 shadow-inner" aria-label={t.name}>
                        <User className="h-5.5 w-5.5 text-slate-500" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-bold text-navy-900">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className={testimonials.length <= 2 ? "mt-8 flex items-center justify-center gap-3 md:hidden" : "mt-8 flex items-center justify-center gap-3"}>
            <CarouselPrevious className="static h-10 w-10 translate-y-0" data-testid="testimonials-prev" />
            <CarouselNext className="static h-10 w-10 translate-y-0" data-testid="testimonials-next" />
          </div>
        </Carousel>
      </Reveal>
    </div>
  </section>
  );
};
